import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { markRaw } from 'vue';

// Constants for player model pool
const DEFAULT_POOL_SIZE = 30;
const TERRAIN_Y_OFFSET = 0.05;

class PlayerPool {
  constructor(scene, modelPath, playerManager, poolSize = DEFAULT_POOL_SIZE) {
    this.scene = scene;
    this.modelPath = modelPath;
    this.poolSize = poolSize;
    this.gltfLoader = markRaw(new GLTFLoader());
    this.availableModels = [];
    this.inUseModels = new Map();
    this.isInitialized = false;
    this.onReadyCallbacks = [];
    
    // Store reference to the PlayerManager for cloning
    this.playerManager = playerManager;
    
    // Store the characterConfig for animation mapping
    this.characterConfig = null;
  }

  /**
   * Initialize the player model pool
   * @param {Object} characterConfig - The character configuration from PlayerManager
   * @returns {Promise} - Resolves when all models are created
   */
  async initialize(characterConfig) {
    console.log(`Initializing player model pool with ${this.poolSize} models...`);
    
    // Store the character config for animation mapping
    this.characterConfig = characterConfig;
    
    // Load the model once
    try {
      const gltf = await this.loadModel(this.modelPath);
      
      // Create pool of cloned models
      for (let i = 0; i < this.poolSize; i++) {
        const playerGroup = this.createPooledModel(gltf, i);
        this.availableModels.push(playerGroup);
      }
      
      this.isInitialized = true;
      console.log(`Player model pool initialized with ${this.availableModels.length} models`);
      
      // Call any registered callbacks
      this.onReadyCallbacks.forEach(callback => callback());
      this.onReadyCallbacks = [];
      
      return true;
    } catch (error) {
      console.error('Failed to initialize player model pool:', error);
      return false;
    }
  }

  /**
   * Load a model from path
   * @param {string} path - Path to the model
   * @returns {Promise} - Promise that resolves with the loaded model
   */
  loadModel(path) {
    return new Promise((resolve, reject) => {
    //  console.log(`PlayerPool loading model from: ${path}`);
      this.gltfLoader.load(
        path,
        (gltf) => {
          console.log(`Successfully loaded model for pool: ${path}`);
          resolve(gltf);
        },
        (progress) => {
          // Optional progress callback
          if (progress.total > 0) {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            if (percent % 10 === 0) { // Log only at 10% intervals to avoid console spam
         //     console.log(`Loading model: ${percent}% (${progress.loaded} / ${progress.total})`);
            }
          }
        },
        (error) => {
          console.error(`Error loading model: ${path}`, error);
          reject(error);
        }
      );
    });
  }

  /**
   * Create a pooled model from the loaded GLTF
   * @param {Object} gltf - The loaded GLTF model
   * @param {number} index - Pool index for debugging
   * @returns {THREE.Group} - The created player group
   */
  createPooledModel(gltf, index) {
    if (!this.playerManager) {
      console.error('PlayerManager not available for cloning');
      return null;
    }
    
    // Create deep clone of the model using PlayerManager's deep clone method
    const clone = this.playerManager.deepCloneGltf(gltf);
    
    // Create a group to hold the model and any attachments
    const playerGroup = markRaw(new THREE.Group());
    
    // Add the model to the group
    playerGroup.add(clone.scene);
    
    // Apply 180-degree rotation to the model scene to match the orientation of non-pooled models
    // This ensures consistent orientation for all player models
    clone.scene.rotation.y = Math.PI; // 180 degrees in radians
    
    // Store the animations with the group
    if (clone.animations.length > 0) {
      // Create animation mixer
      const mixer = new THREE.AnimationMixer(clone.scene);
      playerGroup.userData = {
        mixer,
        animations: {}, // Will hold animation actions
        poolIndex: index,
        isPooled: true,
        isActive: false,
        isPlayer: true, // Important for hit detection
        bones: {} // Will hold bone references for weapon attachment
      };
      
      // Create animation actions and map them by name
      clone.animations.forEach(animation => {
        const action = mixer.clipAction(animation);
        // Store the action with the clip name
        playerGroup.userData.animations[animation.name] = action;
      });
      
      // Map all character animation names if character config is available
      if (this.characterConfig && this.characterConfig.animations) {
        // First, log what animations are available for debugging
       // console.log(`Pooled model ${index} has animations:`, Object.keys(playerGroup.userData.animations));
        
        // Map animations according to the character config
        for (const [animKey, animName] of Object.entries(this.characterConfig.animations)) {
          if (playerGroup.userData.animations[animName]) {
            //console.log(`✅ Found animation for ${animKey} -> ${animName}`);
            
            // Store reference to the animation key for each animation name
            playerGroup.userData[animKey] = animName;
          } else {
            console.warn(`❌ Animation not found for ${animKey} -> ${animName}`);
          }
        }
        
        // Make sure the model has the idle animation mapped and play it to avoid T-pose
        if (playerGroup.userData.animations[this.characterConfig.animations.idle]) {
          const idleAction = playerGroup.userData.animations[this.characterConfig.animations.idle];
          idleAction.reset();
          idleAction.play();
          playerGroup.userData.currentAnimation = 'idle';
         // console.log(`✅ Playing idle animation for pooled model ${index}`);
        } else {
          console.warn(`❌ Could not find idle animation for pooled model ${index}`);
        }
      } else {
        console.warn('No character config available for animation mapping');
      }
      
      // Find and store bone references for weapon attachment
      clone.scene.traverse(object => {
        if (object.isBone) {
          playerGroup.userData.bones[object.name] = object;
        }
        
        // Mark all meshes as player objects for hit detection
        if (object.isMesh) {
          object.userData.isPlayer = true;
          object.userData.poolIndex = index;
          
          // Make hitbox meshes invisible
          if (object.name.startsWith('Hitbox')) {
            object.visible = false;
          }
        }
      });
      
      // Log bone count for debugging
      const boneCount = Object.keys(playerGroup.userData.bones).length;
     // console.log(`Pooled model ${index} has ${boneCount} bones`);
      
      // Check specifically for right hand bone
      const rightHandBone = Object.keys(playerGroup.userData.bones).find(
        name => name.toLowerCase().includes('right') && name.toLowerCase().includes('hand')
      );
      
      if (rightHandBone) {
        //console.log(`✅ Found right hand bone: ${rightHandBone}`);
      } else {
        console.warn(`❌ No right hand bone found in model. Available bones:`, 
          Object.keys(playerGroup.userData.bones).join(', '));
      }
    } else {
      console.warn('Cloned model has no animations');
    }
    
    // Hide the model initially
    playerGroup.visible = false;
    
    // Add to scene immediately but not visible
    if (this.scene) {
      this.scene.add(playerGroup);
    }
    
    return playerGroup;
  }

  /**
   * Get a model from the pool
   * @param {string} id - Unique identifier for the player
   * @returns {THREE.Group|null} - A model from the pool or null if none available
   */
  getModel(id) {
    if (!this.isInitialized) {
      console.warn('Player pool not initialized yet, returning null');
      return null;
    }
    
    // Check if this ID already has a model
    if (this.inUseModels.has(id)) {
      return this.inUseModels.get(id);
    }
    
    // Get a model from the available pool
    if (this.availableModels.length > 0) {
      const model = this.availableModels.pop();
      
      // Mark as in use
      this.inUseModels.set(id, model);
      
      // Prepare model for use
      model.visible = true;
      model.userData.isActive = true;
      
     // console.log(`Assigned pooled model ${model.userData.poolIndex} to player ${id}`);
      return model;
    }
    
    console.warn('No available models in the pool!');
    return null;
  }

  /**
   * Return a model to the pool
   * @param {string} id - ID of the player returning the model
   */
  returnModel(id) {
    if (this.inUseModels.has(id)) {
      const model = this.inUseModels.get(id);
      
      // Reset model state
      this.resetModel(model);
      
      // Remove from in-use list
      this.inUseModels.delete(id);
      
      // Add back to available pool
      this.availableModels.push(model);
      
      console.log(`Returned model ${model.userData.poolIndex} to pool from player ${id}`);
    }
  }

  /**
   * Reset a model to its initial state
   * @param {THREE.Group} model - The model to reset
   */
  resetModel(model) {
    // Hide the model
    model.visible = false;
    
    // Reset position and rotation of the group
    model.position.set(0, TERRAIN_Y_OFFSET, 0);
    model.rotation.set(0, 0, 0);
    
    // Make sure the model scene's rotation is preserved (180 degrees)
    // Find the model scene (first child)
    if (model.children.length > 0) {
      const modelScene = model.children[0];
      // Ensure the model scene maintains its 180-degree rotation
      modelScene.rotation.y = Math.PI;
    }
    
    // Stop all animations
    if (model.userData && model.userData.mixer) {
      model.userData.mixer.stopAllAction();
    }
    
    // Preserve important parts of userData
    const { mixer, animations, poolIndex, bones } = model.userData;
    const animationReferences = {};
    
    // Preserve animation reference mapping
    if (this.characterConfig && this.characterConfig.animations) {
      Object.keys(this.characterConfig.animations).forEach(animKey => {
        if (model.userData[animKey]) {
          animationReferences[animKey] = model.userData[animKey];
        }
      });
    }
    
    // Reset userData with preserved values
    model.userData = {
      mixer,
      animations,
      poolIndex,
      bones, // Keep bones reference for weapon attachment
      isPooled: true,
      isActive: false,
      isPlayer: true, // Important for hit detection
      ...animationReferences
    };
    
    // Remove any child objects that might have been added (like weapons)
    // First, find the original model (first child)
    const originalModel = model.children[0];
    
    // Remove all additional children (keep the first one which is the original model)
    while (model.children.length > 1) {
      const child = model.children[1];
      model.remove(child);
    }
    
    // Check if the originalModel exists and reset its userData
    if (originalModel && originalModel.userData) {
      const cleanUserData = {};
      // Copy only necessary properties, avoid circular references
      if (originalModel.userData.name) cleanUserData.name = originalModel.userData.name;
      if (originalModel.userData.type) cleanUserData.type = originalModel.userData.type;
      cleanUserData.isPlayer = true; // Important for hit detection
      cleanUserData.poolIndex = poolIndex;
      
      // Apply the cleaned userData
      originalModel.userData = cleanUserData;
      
      // Also make sure all mesh children have isPlayer set
      originalModel.traverse(object => {
        if (object.isMesh) {
          object.userData.isPlayer = true;
          object.userData.poolIndex = poolIndex;
          
          // Make sure hitbox meshes stay invisible but accessible to the raycaster
          if (object.name.startsWith('Hitbox')) {
            // Make invisible to camera but keep them for hit detection
            object.visible = false;
            
            // Important: Setting visible to false makes the object not raycastable
            // We need to ensure these hitboxes are still raycastable
            // This will be handled by the WeaponSystem when doing hit detection
          }
        }
      });
    }
  }

  /**
   * Register a callback for when the pool is ready
   * @param {Function} callback - Function to call when pool is ready
   */
  onReady(callback) {
    if (this.isInitialized) {
      callback();
    } else {
      this.onReadyCallbacks.push(callback);
    }
  }

  /**
   * Get the number of available models
   * @returns {number} - Count of available models
   */
  get availableCount() {
    return this.availableModels.length;
  }

  /**
   * Get the number of in-use models
   * @returns {number} - Count of in-use models
   */
  get inUseCount() {
    return this.inUseModels.size;
  }

  /**
   * Clean up all resources
   */
  dispose() {
    // Clean up all models
    [...this.availableModels, ...this.inUseModels.values()].forEach(model => {
      // Stop animations
      if (model.userData && model.userData.mixer) {
        model.userData.mixer.stopAllAction();
      }
      
      // Remove from scene
      if (this.scene) {
        this.scene.remove(model);
      }
    });
    
    // Clear arrays
    this.availableModels = [];
    this.inUseModels.clear();
    this.isInitialized = false;
    
    // Clear references
    this.playerManager = null;
    this.characterConfig = null;
  }
}

export default PlayerPool; 